import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./adminlogin.css";
import {
  formatPhoneNumber,
  formatDate,
  formatEventDate,
  formatArrayField,
  fetchWithCORS,
} from "./utils";
import { API_BASE_URL } from "./constants";
import { UserEditModal, DeleteConfirmModal } from "./components/modals";
import { LoginForm } from "./components/auth";
import { UserManager } from "./components/apps";
import { EventsTab } from "./components/events";
import MobileTabNav from "../../common/MobileTabNav";
import { PendingRequestsTab } from "./components/pendingRequests";
import { ChatTab } from "./components/chat";
import { AccountTab } from "./components/account";
import AppProviders from "../../../context/AppProviders";
import { useAuth } from "../../../context/AuthContext";
import PhotoUploadTab from "./PhotoUploadTab";
import PhotoManagerTab from "./PhotoManagerTab";
import LocationsTab from "./LocationsTab";

// Helper function has been moved to utils.js

// Helper function has been moved to utils.js

// API_URL and ENABLE_SIGNUP have been moved to constants.js

// Cognito configuration - set these after running the Cognito setup scripts
window.COGNITO_CONFIG = {
  userPoolId: "us-east-1_fc1omeTGh",
  clientId: "54fiucjkk95o6gna3l5l3dnjuo",
  region: "us-east-1",
};

// Admin emails that should always have access regardless of role
const ADMIN_EMAILS = [
  "davidhudman@gmail.com",
  "admin@activebeaches.com",
  "test@activebeaches.com",
  "test@test.com",
  "david@activebeaches.com",
  // Add any admin emails here
];

/**
 * AdminLogin component now uses the AppProviders context
 */
export default function AdminLogin() {
  const navigate = useNavigate();

  // Use the auth context
  const { isAuthenticated, userInfo, userRole, logout, getAuthHeaders } = useAuth();

  // Debug state to show role info and allow override
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const [adminOverride, setAdminOverride] = useState(false);

  // State for the active tab and app
  const [activeTab, setActiveTab] = useState("pendingRequests");
  const [activeApp, setActiveApp] = useState("userManager");

  // State for user edit modal
  const [userEditModal, setUserEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userEditFields, setUserEditFields] = useState({});
  const [userEditSaving, setUserEditSaving] = useState(false);
  const [userEditMessage, setUserEditMessage] = useState("");

  // State for delete confirm modal
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState("");

  // Helper function to check if user should have admin access
  const hasAdminAccess = () => {
    // Check for admin override
    if (adminOverride) return true;

    // Check if email is in admin list
    if (userInfo && userInfo.email && ADMIN_EMAILS.includes(userInfo.email)) {
      return true;
    }

    // Check Cognito role
    return userRole === "admin";
  };

  // Log authentication info for debugging
  useEffect(() => {
    if (isAuthenticated) {
      console.log("Auth Debug Info:", {
        isAuthenticated,
        userRole,
        email: userInfo?.email,
        groups: userInfo?.groups,
        hasAdminAccess: hasAdminAccess(),
      });
    }
  }, [isAuthenticated, userInfo, userRole]);

  // Check if user is an admin and redirect if not
  useEffect(() => {
    if (isAuthenticated && userInfo && !hasAdminAccess()) {
      // Redirect non-admin authenticated users to the dashboard
      navigate("/dashboard");
    }
  }, [isAuthenticated, userInfo, userRole, navigate, adminOverride]);

  /**
   * Navigate to GraphQL chats
   */
  const navigateToGraphQLChats = () => {
    navigate("/admin/chats");
  };

  /**
   * Handle app change
   * @param {string} app - The app to switch to
   */
  const handleAppChange = (app) => {
    setActiveApp(app);
  };

  /**
   * Handle user edit
   * @param {Object} user - The user to edit
   */
  const handleUserEdit = (user) => {
    setEditingUser(user);

    // Only include fields that actually exist in the database and are shown in the modal
    const fields = {
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      email: user.email || "",
      phone: user.phone || "",
      zsex: user.zsex || "",
      groupMeUserId: user.groupMeUserId || "",
      shirtsize: user.shirtsize || "",
      referredBy: user.referredBy || "",
      reInterestedInEvents: user.reInterestedInEvents || "",
      selectedEvents: user.selectedEvents || "",
      zmessage: user.zmessage || "",
      socials: user.socials || "",
      zphone2: user.zphone2 || "",
      // Note: dateAdded is disabled in the modal, so we don't include it here
    };

    setUserEditFields(fields);
    setUserEditMessage("");
    setUserEditModal(true);
  };

  /**
   * Handle user edit field change
   * @param {string} field - The field to change
   * @param {any} value - The new value
   */
  const handleUserEditFieldChange = (field, value) => {
    setUserEditFields((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  /**
   * Save user edit
   */
  const saveUserEdit = async () => {
    setUserEditSaving(true);
    setUserEditMessage("");

    try {
      // Only send fields that have actually changed
      const changedFields = {};
      Object.keys(userEditFields).forEach(field => {
        let originalValue = editingUser[field] || "";
        const newValue = userEditFields[field] || "";
        
        // Handle array fields that might be stored as JSON or comma-separated strings
        if (["reInterestedInEvents", "selectedEvents"].includes(field)) {
          if (Array.isArray(originalValue)) {
            originalValue = originalValue.join(", ");
          } else if (typeof originalValue === "string" && originalValue.startsWith("[")) {
            try {
              const parsed = JSON.parse(originalValue);
              if (Array.isArray(parsed)) {
                originalValue = parsed.join(", ");
              }
            } catch (e) {
              // Keep as string if parsing fails
            }
          }
        }
        
        if (originalValue !== newValue) {
          changedFields[field] = newValue;
        }
      });

      // If no fields changed, don't make the API call
      if (Object.keys(changedFields).length === 0) {
        setUserEditMessage("No changes to save");
        setUserEditSaving(false);
        return;
      }

      // Create a data object that includes both the user ID and only changed fields
      const dataToSend = {
        type: "editUser",
        userId: editingUser.userId,
        fields: changedFields,
      };

      console.log(
        "Saving user with data:",
        JSON.stringify(dataToSend, null, 2)
      );

      const response = await fetchWithCORS(API_BASE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify(dataToSend),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("API Error:", response.status, errorText);
        throw new Error(`Failed to update user (${response.status})`);
      }

      setUserEditMessage("User updated successfully");
      setTimeout(() => {
        setUserEditModal(false);
        setUserEditMessage("");
        // Refresh or update the user list after successful edit
        window.location.reload();
      }, 1500);
    } catch (err) {
      console.error("Error saving user:", err);
      setUserEditMessage("Error: " + (err.message || "Failed to update user"));
    } finally {
      setUserEditSaving(false);
    }
  };

  /**
   * Handle close user edit modal
   */
  const handleCloseUserEditModal = () => {
    setUserEditModal(false);
    setUserEditMessage("");
  };

  /**
   * Handle delete user
   * @param {Object} user - The user to delete
   */
  const handleDeleteUser = (user) => {
    setUserToDelete(user);
    setDeleteConfirmText("");
    setDeleteMessage("");
    setDeleteConfirmModal(true);
  };

  /**
   * Confirm delete user
   */
  const confirmDeleteUser = async () => {
    if (deleteConfirmText !== "DELETE") {
      setDeleteMessage("Please type DELETE to confirm");
      return;
    }

    setIsDeleting(true);
    setDeleteMessage("");

    try {
      const response = await fetchWithCORS(API_BASE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          type: "deleteUsers",
          userIds: [userToDelete.userId],
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("API Error:", response.status, errorText);
        throw new Error(`Failed to delete user (${response.status})`);
      }

      const result = await response.json();
      
      if (result.success) {
        setDeleteMessage("User deleted successfully");
        setTimeout(() => {
          setDeleteConfirmModal(false);
          setDeleteMessage("");
          // Close edit modal if it was open
          setUserEditModal(false);
          setUserEditMessage("");
          // Refresh the user list
          window.location.reload();
        }, 1500);
      } else {
        throw new Error(result.message || "Failed to delete user");
      }
    } catch (err) {
      console.error("Error deleting user:", err);
      setDeleteMessage("Error: " + (err.message || "Failed to delete user"));
    } finally {
      setIsDeleting(false);
    }
  };

  /**
   * Handle close delete modal
   */
  const handleCloseDeleteModal = () => {
    setDeleteConfirmModal(false);
    setDeleteMessage("");
  };

  /**
   * Handle logout
   */
  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  /**
   * Handle login success
   */
  const handleLoginSuccess = () => {
    // If the user is an admin, they'll stay on this page
    // If not, the useEffect hook will redirect them
  };

  // If not logged in, show login form
  if (!isAuthenticated) {
    return <LoginForm onLoginSuccess={handleLoginSuccess} />;
  }

  // Debug info component
  const renderDebugInfo = () => {
    if (!showDebugInfo) return null;

    return (
      <div
        className="debug-info"
        style={{
          background: "#f8f9fa",
          padding: "10px",
          margin: "10px 0",
          border: "1px solid #dee2e6",
          borderRadius: "4px",
        }}
      >
        <h4>Admin Authorization Debug</h4>
        <p>User Email: {userInfo?.email || "Unknown"}</p>
        <p>User Role: {userRole || "None"}</p>
        <p>User Groups: {JSON.stringify(userInfo?.groups || [])}</p>
        <p>Has Admin Access: {hasAdminAccess() ? "Yes" : "No"}</p>
        <p>Admin Override: {adminOverride ? "Enabled" : "Disabled"}</p>
        <button
          onClick={() => setAdminOverride(!adminOverride)}
          style={{
            background: adminOverride ? "#28a745" : "#dc3545",
            color: "white",
            border: "none",
            padding: "5px 10px",
            borderRadius: "4px",
            marginRight: "10px",
          }}
        >
          {adminOverride ? "Disable Override" : "Enable Admin Override"}
        </button>
      </div>
    );
  };

  return (
    <div className="admin-login-container">
      <div className="admin-content">
        {/* Debug control button */}
        <div style={{ position: "absolute", top: "10px", right: "10px" }}>
          <button
            onClick={() => setShowDebugInfo(!showDebugInfo)}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              fontSize: "20px",
            }}
            title="Toggle Debug Info"
          >
            🔧
          </button>
        </div>

        {/* Debug info panel */}
        {renderDebugInfo()}

        <UserEditModal
          isOpen={userEditModal}
          user={editingUser}
          userFields={userEditFields}
          onFieldChange={handleUserEditFieldChange}
          isSaving={userEditSaving}
          message={userEditMessage}
          onSave={saveUserEdit}
          onCancel={handleCloseUserEditModal}
          onDelete={handleDeleteUser}
        />

        <DeleteConfirmModal
          isOpen={deleteConfirmModal}
          user={userToDelete}
          confirmText={deleteConfirmText}
          onConfirmTextChange={setDeleteConfirmText}
          isDeleting={isDeleting}
          deleteMessage={deleteMessage}
          onConfirm={confirmDeleteUser}
          onCancel={handleCloseDeleteModal}
        />

        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button
            className={activeTab === "pendingRequests" ? "active" : ""}
            onClick={() => setActiveTab("pendingRequests")}
          >
            Pending Requests
          </button>
          <button
            className={activeTab === "events" ? "active" : ""}
            onClick={() => setActiveTab("events")}
          >
            Social Events
          </button>
          <button
            className={activeTab === "apps" ? "active" : ""}
            onClick={() => setActiveTab("apps")}
          >
            Apps
          </button>
          <button
            className={activeTab === "chat" ? "active" : ""}
            onClick={() => setActiveTab("chat")}
          >
            Chat
          </button>
          <button
            className={activeTab === "account" ? "active" : ""}
            onClick={() => setActiveTab("account")}
          >
            Account
          </button>
          <button
            className={activeTab === "photoUpload" ? "active" : ""}
            onClick={() => setActiveTab("photoUpload")}
          >
            Upload Photo
          </button>
          <button
            className={activeTab === "photoManager" ? "active" : ""}
            onClick={() => setActiveTab("photoManager")}
          >
            Photos
          </button>
          <button
            className={activeTab === "locations" ? "active" : ""}
            onClick={() => setActiveTab("locations")}
          >
            Locations
          </button>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {/* Pending Requests Tab */}
          {activeTab === "pendingRequests" && (
            <div className="tab-content">
              <PendingRequestsTab />
            </div>
          )}

          {/* Events Tab */}
          {activeTab === "events" && (
            <div className="tab-content">
              <EventsTab />
            </div>
          )}

          {/* Apps Tab - NEW */}
          {activeTab === "apps" && (
            <div className="apps-tab">
              {/* Apps Navigation */}
              <div className="apps-navigation">
                <button
                  className={activeApp === "userManager" ? "active" : ""}
                  onClick={() => handleAppChange("userManager")}
                >
                  <span className="app-icon">👥</span>
                  <span className="app-name">User Manager</span>
                </button>
                {/* Add more app buttons here in the future */}
              </div>

              {/* Apps Content */}
              {activeApp === "userManager" && (
                <UserManager onUserEdit={handleUserEdit} />
              )}
            </div>
          )}

          {/* Chat Tab */}
          {activeTab === "chat" && (
            <div className="tab-content">
              <ChatTab onNavigateToGraphQLChats={navigateToGraphQLChats} />
            </div>
          )}

          {/* Account Tab */}
          {activeTab === "account" && (
            <div className="tab-content">
              <AccountTab />
            </div>
          )}

          {/* Photo Upload Tab */}
          {activeTab === "photoUpload" && (
            <div className="tab-content">
              <PhotoUploadTab />
            </div>
          )}

          {/* Photo Manager Tab */}
          {activeTab === "photoManager" && (
            <div className="tab-content">
              <PhotoManagerTab />
            </div>
          )}

          {/* Locations Tab */}
          {activeTab === "locations" && (
            <div className="tab-content">
              <LocationsTab />
            </div>
          )}
        </div>
      </div>

      {/* Mobile Tab Navigation */}
      <MobileTabNav
        tabs={[
          { id: "pendingRequests", icon: "📋", label: "Pending" },
          { id: "events", icon: "🗓️", label: "Events" },
          { id: "apps", icon: "🧩", label: "Apps" },
          { id: "chat", icon: "💬", label: "Chat" },
          { id: "account", icon: "👤", label: "Account" },
        ]}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
    </div>
  );
}

/**
 * Wrap the AdminLogin component with AppProviders
 */
export function AdminLoginWithProviders() {
  return (
    <AppProviders>
      <AdminLogin />
    </AppProviders>
  );
}
